import { useEffect, useReducer, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { addNegativeToast } from 'modules/toastContainer';

import { useGetTemplateHistory } from 'api/TemplateHistoryAPI';
import {
  TemplateHistoryContext,
  defaultHistoryContextState,
  TemplateHistoryContextState,
} from './TemplateHistoryContext';
import {
  templateHistoryContextReducer,
  dispatchTemplateId,
  dispatchState,
} from './templateHistoryContextReducer';

type TemplateHistoryContextProviderProps = {
  children: ReactNode;
  /**
   * default context value to use. If not provided, defaultHistoryContextState will be used.
   * Providing a param is useful for testing purposes.
   */
  value?: TemplateHistoryContextState;
};

export const TemplateHistoryContextProvider = ({
  children,
  value = defaultHistoryContextState,
}: TemplateHistoryContextProviderProps) => {
  const reduxDispatch = useDispatch();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const searchParamsTemplateId = searchParams.get('templateId');

  const [context, contextDispatch] = useReducer(templateHistoryContextReducer, value);

  // we can't actually reach this state in the app right now. the backend will return a 404 if
  // the templateId is not found, so we don't need to handle it here. however, this remains a
  // useful thing to throw if we ever need to handle a missing templateId in the future.
  // (and typescript yells at us if we don't do this)
  if (searchParamsTemplateId === null) {
    const err = new Error('templateId is required in the URL query parameters');
    reduxDispatch(addNegativeToast('Failed to fetch template history', err));
    throw err;
  }

  const { data, isLoading, refetch } = useGetTemplateHistory(searchParamsTemplateId, {
    onError: (err) => {
      reduxDispatch(addNegativeToast('Failed to fetch template history', err));
    },
  });

  useEffect(() => {
    if (searchParamsTemplateId) {
      contextDispatch(dispatchTemplateId(searchParamsTemplateId));
    }
  }, [searchParamsTemplateId]);

  useEffect(() => {
    contextDispatch(
      dispatchState(isLoading, data?.metadata, refetch, data?.template, data?.templateContentIds),
    );
  }, [data, isLoading, refetch]);

  return (
    <TemplateHistoryContext.Provider value={context}>{children}</TemplateHistoryContext.Provider>
  );
};
